import ApplicationController from "../../application_component_controller"

import URL from "hb/lib/url"
import { useOutlets } from "hb/insights/use_outlets"
import { fetchJSON } from "hb/lib/fetch"
import { DEFAULT_TS_PARAM } from "hb/insights/globals"
import { debounce } from "hb/lib/utils"

import ASTBuilder from "hb/insights/badgersense/ast_builder"
import Suggester from "hb/insights/badgersense/suggester"
import OpticonChannel from "hb/insights/badgersense/opticon_channel"

export default class extends ApplicationController {
  static targets = [
    "badgersense", "listSuggestionTemplate", "regexSuggestionTemplate", "headerSuggestionTemplate"
  ]

  static values = {
    opticonWsUrl: String,
    opticonWsTokenUrl: String,
    badgersenseKeyLookupPath: String,
    wsDisabled: Boolean,
    streamIds: Array
  }

  connect() {
    useOutlets(this)

    if (!this.wsDisabledValue) {
      this.opticonChannel = new OpticonChannel(
        this.opticonWsUrlValue,
        this.opticonWsTokenUrlValue,
        this.onLookedUpFields.bind(this)
      );
      this.debouncedFetchFieldKeys = debounce((channel, token, ts, streamIds) => {
        if (channel.connected) {
          channel.fieldKeysWs.send({ token, ts, streamIds })
        }
      }, 200)
      this.opticonChannel.wsAttemptConnect()
    }

    this.listSuggestionTemplate = this.compileElement(this.listSuggestionTemplateTarget)
    this.regexSuggestionTemplate = this.compileElement(this.regexSuggestionTemplateTarget)
    this.headerSuggestionTemplate = this.compileElement(this.headerSuggestionTemplateTarget)

    this.offset = 0;
    this.suggestions = {};
    this.updateParams();

    this.updateParamsHandler = this.updateParams.bind(this)
    window.addEventListener("urlchanged", this.updateParamsHandler)

    this.badgersenseMeta;
  }

  disconnect() {
    window.removeEventListener("urlchanged", this.updateTsHandler)
    if (!this.wsDisabledValue) {
      this.opticonChannel.close();
    }
  }

  updateParams() {
    this.ts = URL.params().get("ts") || DEFAULT_TS_PARAM;
    if (URL.params().has("stream_ids")) {
      this.streamIds = JSON.parse(URL.params().get("stream_ids"));
    } else {
      this.streamIds = this.streamIdsValue;
    }
  }

  setupCallbacks(cbs) {
    this.queryComponent = cbs
  }

  get currentSuggestion() {
    const sel = this.find("li.selected")
    const idx = Array.from(sel.parentNode.children).indexOf(sel)
    return this.suggestions?.results[idx]
  }

  get currentSelection() {
    return this.suggester?.selection
  }

  get hasSuggestions() {
    return this.suggestions?.results?.length > 0
  }

  moveSelection(pos) {
    const selected = this.find(".suggestions li.selected");
    const items = Array.from(this.findAll(".suggestions li"));
    const idx = items.indexOf(selected);
    if(items.length < 2) return;

    if(items[idx + pos] && !items[idx + pos]?.classList?.contains("header")) {
      selected?.classList?.remove("selected");
      items[idx + pos].classList.add("selected");
    } else if(items[idx + pos + pos]) {
      selected?.classList?.remove("selected");
      items[idx + pos + pos].classList.add("selected");
    }
  }

  onSuggestionSelect({target}) {
    if(target.classList.contains("doc-link")) {
      this.queryComponent.onDocLinkSelect(target.dataset.docLink)
    } else {
      const idx = Array.from(this.find(".suggestions").children).indexOf(target)
      this.queryComponent.onSuggestionSelect(this.suggestions?.results[idx])
    }
  }

  onLookedUpFields({token, results}) {
    if (this.currentLookupToken !== token) return;

    let items = this.find(".suggestions")
    let needsPlacement = false;

    if(!items) {
      needsPlacement = true;
      items = document.createElement('ul');
      items.classList.add('suggestions');
    }

    results.reverse().forEach(({key, type}) => {
      const li = document.createElement('li');
      const rest = key.slice(token.length);
      const matched = key.slice(0, token.length);
      li.innerHTML = this.listSuggestionTemplate({kind: "field", matched, rest, badge: type});
      li.addEventListener("click", this.onSuggestionSelect.bind(this));
      li.classList.add("field");
      items.prepend(li);
      this.suggestions.results.unshift({
        kind: "field", type, rest, matched
      });
    });

    if (results.length > 0) {
      this.find(".suggestions li.selected")?.classList?.remove("selected");
      if (items.firstChild.classList.contains("header")) {
        items.children[1].classList.add("selected");
      } else {
        items.firstChild.classList.add("selected");
      }
      this.queryComponent.hasSuggestions();
    }

    if(needsPlacement) {
      this.badgersenseTarget.appendChild(items);
    }
  }

  suggest(query, cursorOffset, fromInput = false) {
    if (!this.badgersenseMeta) return;

    const astBuilder = new ASTBuilder(query);
    astBuilder.process();
    this.suggester = new Suggester(astBuilder, cursorOffset, this.badgersenseMeta, fromInput);
    const suggestions = this.suggester.suggestions();
    if (!suggestions) return;

    if (suggestions.lookupFields && this.debouncedFetchFieldKeys) {
      this.currentLookupToken = suggestions.token;
      this.debouncedFetchFieldKeys(this.opticonChannel, this.currentLookupToken, this.ts, this.streamIds);
    }

    this.suggestions = suggestions;
    this.offset = suggestions.offset;

    const items = document.createElement('ul');
    suggestions?.results?.forEach(data => {
      const li = document.createElement('li');
      const {matched, rest, kind} = data;

      if(data.structure) {
        data.subtext = data.structure
      } else if (data.examples) {
        data.subtext = data.examples[0]
      }

      if(kind === "exprFn") {
        data.doclink = "expr-" + matched + rest;
        data.subbadge = data.group;
      } else if (kind === "badgerFn") {
        data.doclink = matched + rest;
      }

      if (data.html) {
        li.innerHTML = data.html;
      } else {
        li.innerHTML = this[`${data.template || suggestions.group || "list"}SuggestionTemplate`](data);
      }
      li.addEventListener("click", this.onSuggestionSelect.bind(this));
      li.classList.add(kind);
      items.appendChild(li);
    })

    // Do nothing if there are no items
    if (items.children.length > 0) {
      if (items.firstChild.classList.contains("header")) {
        items.children[1].classList.add("selected");
      } else {
        items.firstChild.classList.add("selected");
      }

      this.queryComponent.hasSuggestions();
    }

    items.classList.add("suggestions");
    if (suggestions?.group === "regex") {
      items.classList.add("regex-suggestions");
    }

    this.reposition(items);
    this.badgersenseTarget.appendChild(items);

    return suggestions
  }

  clear() {
    this.badgersenseTarget.querySelectorAll("li").forEach(el => {
      el.removeEventListener("click", this.onSuggestionSelect.bind(this));
    });
    this.badgersenseTarget.innerHTML = "";
  }

  reposition(suggestions = this.find(".suggestions")) {
    if (!this.queryComponent) return;
    const {left, bottom, width} = this.queryComponent.positions(this.offset);
    this.element.style.left = `${left}px`;
    this.element.style.top = `${bottom}px`;

    if(suggestions) {
      suggestions.style.maxWidth = `${width}px`;
    }
  }

  fetchFieldKeys(token) {
    fetchJSON({
      path: this.badgersenseKeyLookupPathValue,
      method: "GET",
      params: {token, ts: this.ts}
    }).then(({results}) => {
      if (results.length === 0) return;
      this.onLookedUpFields(token, results)
    })
  }
}
